import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import * as schemas from '../../../assets/schemas/schemas';
import { LoginForm } from '../../common/Forms/Form';
import { LogoButton, ActionButton, AltButton } from '../../common/Buttons/Buttons';
import logo from '../../../images/logo.svg';
import { loginService } from '../../../services/loginService.js';
import { notify } from '../../../assets/helpers/toast';
import { LoadSpinner } from '../../common/LoadSpinner/LoadSpinner';
import '../Login/Login.scss';
import './DeleteAccount.scss';

export const DeleteAccount = () => {
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
  } = useForm({
    resolver: yupResolver(schemas.LoginSchema(t)),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const [isLoading, setIsLoading] = useState(false);
  const [accountDeleted, setAccountDeleted] = useState(false);
  const [confirmationPending, setConfirmationPending] = useState(false);
  const [pendingAccessToken, setPendingAccessToken] = useState(null);


  const onSubmit = handleSubmit(async (data) => {
    setIsLoading(true);
    try {
      const loginResponse = await loginService.login(data);

      if (loginResponse.status === 200) {
        const accessToken =
          loginResponse?.data?.tokens?.accessToken ||
          loginResponse?.data?.accessToken;
        setPendingAccessToken(accessToken);
        setConfirmationPending(true);
      } else {
        notify(
          loginResponse.message || 'Usuario o contraseña incorrectos',
          'error'
        );
        setValue('password', '');
      }
    } catch (error) {
      notify('Error inesperado. Intente nuevamente.', 'error');
    } finally {
      setIsLoading(false);
    }
  });

  const handleConfirmDelete = async () => {
    if (!pendingAccessToken) return;
    setIsLoading(true);
    try {
      const deleteResponse = await loginService.deleteAccount(pendingAccessToken);
      if (deleteResponse.status === 200) {
        setAccountDeleted(true);
        setConfirmationPending(false);
        setPendingAccessToken(null);
        notify('Cuenta eliminada exitosamente', 'success');
      } else {
        notify(
          deleteResponse.message || 'Error al eliminar la cuenta',
          'error'
        );
      }
    } catch (error) {
      notify('Error inesperado. Intente nuevamente.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelConfirm = () => {
    setConfirmationPending(false);
    setPendingAccessToken(null);
    reset({ email: '', password: '' });
  };

  if (accountDeleted) {
    return (
      <div className="container view-content">
        <div className="row">
          <div className="col-12 login-page-container">
            <div className="login-form">
              <div className="login-logo-container">
                <LogoButton link={'/'} src={logo} />
                <br />
                <h5>Cuenta eliminada</h5>
                <div className="horizontal-bar-container">
                  <div></div>
                </div>
              </div>
              <div className="account-deleted-message">
                <p>Su cuenta ha sido eliminada exitosamente.</p>
                <p>Gracias por haber utilizado nuestros servicios.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (confirmationPending) {
    return (
      <div className="container view-content">
        <div className="row">
          <div className="col-12 login-page-container">
            <div className="login-form">
              <div className="login-logo-container">
                <LogoButton link={'/'} src={logo} />
                <br />
                <h5>Confirmar eliminación</h5>
                <div className="horizontal-bar-container">
                  <div></div>
                </div>
              </div>
              <div className="delete-account-confirmation">
                <p>
                  ¿Está seguro de que desea eliminar su cuenta? Esta acción no se
                  puede deshacer y se perderán todos sus datos de forma
                  permanente.
                </p>
                <div className="delete-account-confirmation-buttons">
                  <AltButton label="Cancelar" action={handleCancelConfirm} />
                  <ActionButton
                    label="Confirmar eliminación"
                    action={handleConfirmDelete}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <LoadSpinner label={'Verificando credenciales'} show={isLoading} />
      <div className="container view-content">
        <div className="row">
          <div className="col-12 login-page-container">
            <div className="login-form">
              <div className="login-logo-container">
                <LogoButton link={'/'} src={logo} />
                <br />
                <h5>Eliminar cuenta</h5>
                <div className="horizontal-bar-container">
                  <div></div>
                </div>
              </div>
              <p className="delete-account-description">
                Ingrese sus credenciales para confirmar la eliminación de su
                cuenta. Esta acción no se puede deshacer.
              </p>
              <LoginForm
                t={t}
                register={register}
                errors={errors}
                action={onSubmit}
                submitLabel="Continuar"
              />
              <div className="delete-account-footer">
                <a
                  href="https://tugruero.com/?page_id=3"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Políticas de privacidad
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
